import type { ReactNode } from 'react';

import type { DetailRow } from '../model/selection.js';

/**
 * The selection detail block: a heading, a status chip, and a key/value table
 * (design 1288–1348).
 *
 * The ledger's second row, lifted by Phase 4's MW detail panel. So it takes
 * rows rather than a subject: an MW track has none of an asset's fields and all
 * of a detail panel's shape, and a component that switched on the subject would
 * have to grow a fourth arm to be lifted at all.
 *
 * **Nothing here is defaulted** (M5-D10). A caller with no value for a row does
 * not pass the row; there is no `'—'` written in this file, because a dash the
 * component invented and a dash the projection sent are the same pixels.
 */

export interface DetailBlockProps {
  readonly id: string;
  readonly idColour?: string;
  readonly chip?: { readonly text: string; readonly colour: string };
  readonly name: string;
  readonly kindLabel: string;
  readonly symbol?: ReactNode;
  readonly rows: readonly DetailRow[];
  readonly children?: ReactNode;
}

export function DetailBlock(props: DetailBlockProps): JSX.Element {
  return (
    <div
      data-testid="detail-block"
      style={{ flex: 'none', display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          gap: 10,
          padding: '14px 14px 12px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {props.symbol}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              data-testid="detail-id"
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--font-data)',
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '.06em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: props.idColour ?? 'var(--text)',
              }}
            >
              {props.id}
            </span>
            {props.chip !== undefined && (
              <span
                data-testid="detail-chip"
                style={{
                  flex: 'none',
                  padding: '3px 8px',
                  border: '1px solid ' + props.chip.colour,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-data)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  color: props.chip.colour,
                }}
              >
                {props.chip.text}
              </span>
            )}
          </div>
          {/* Dropped rather than drawn blank when there is nothing to put in it:
              an orbital band's id *is* its name, and a blank line under `GEO`
              would be the panel making room for a word the wire does not have.
              The same is true when the two differ only in case — a region whose
              name is its id in title case put `BOREA` directly over `Borea`. */}
          {props.name !== '' && props.name.toUpperCase() !== props.id.toUpperCase() && (
            <div
              data-testid="detail-name"
              style={{ fontFamily: 'var(--font-prose)', fontSize: 13, color: 'var(--text-2)' }}
            >
              {props.name}
            </div>
          )}
          <div
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            {props.kindLabel}
          </div>
        </div>
      </div>

      {props.children}

      {props.rows.length > 0 && (
        <div
          data-testid="detail-rows"
          style={{
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            padding: '4px 14px 10px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {props.rows.map((row) => (
            <div
              key={row.key}
              data-testid="detail-row"
              data-row-key={row.key}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 12,
                padding: '6px 0',
                borderBottom: '1px solid var(--rule-faint)',
              }}
            >
              <span
                style={{
                  flex: 'none',
                  fontFamily: 'var(--font-data)',
                  fontSize: 11,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                }}
              >
                {row.key}
              </span>
              <span
                data-testid="detail-value"
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'right',
                  fontFamily: 'var(--font-data)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  wordBreak: 'break-word',
                  color: toneColour(row.tone),
                }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function toneColour(tone: DetailRow['tone']): string {
  switch (tone) {
    case 'red':
      return 'var(--red-text)';
    case 'warn':
      return 'var(--warn)';
    default:
      return 'var(--text-2)';
  }
}
